import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, Loader2, Star, Clock, MessageCircle, ChevronLeft, ChevronRight, Utensils, Building2, ArrowRight, Plane, Users, Filter, SlidersHorizontal } from 'lucide-react';

/* ── Reference Data ── */
const KZ_DEPARTURES = [
  { id: 60, name: 'Алматы' }, { id: 59, name: 'Астана' }, { id: 79, name: 'Шымкент' },
  { id: 76, name: 'Караганда' }, { id: 73, name: 'Актобе' }, { id: 74, name: 'Актау' },
  { id: 75, name: 'Атырау' }, { id: 84, name: 'Уральск' }, { id: 68, name: 'Костанай' },
  { id: 66, name: 'Павлодар' }, { id: 78, name: 'Усть-Каменогорск' }, { id: 121, name: 'Семей' },
  { id: 97, name: 'Тараз' }, { id: 77, name: 'Кызылорда' }, { id: 98, name: 'Петропавловск' },
  { id: 120, name: 'Туркестан' },
];
const COUNTRIES = [
  { code: 4, name: 'Турция', flag: '🇹🇷' }, { code: 1, name: 'Египет', flag: '🇪🇬' },
  { code: 9, name: 'ОАЭ', flag: '🇦🇪' }, { code: 2, name: 'Таиланд', flag: '🇹🇭' },
  { code: 8, name: 'Мальдивы', flag: '🇲🇻' }, { code: 12, name: 'Шри-Ланка', flag: '🇱🇰' },
  { code: 16, name: 'Вьетнам', flag: '🇻🇳' }, { code: 7, name: 'Индонезия', flag: '🇮🇩' },
  { code: 10, name: 'Куба', flag: '🇨🇺' }, { code: 13, name: 'Китай', flag: '🇨🇳' },
  { code: 41, name: 'Танзания', flag: '🇹🇿' }, { code: 28, name: 'Сейшелы', flag: '🇸🇨' },
];
const MEAL_OPTIONS = [
  { value: 0, label: 'Любое' }, { value: 7, label: 'Все включено' },
  { value: 4, label: 'Полупансион' }, { value: 3, label: 'Завтрак' },
  { value: 2, label: 'Без питания' },
];
const MONTH_NAMES = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const DAY_NAMES = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

/* ── Types ── */
interface ParsedTour {
  id: string; hotelId: number; hotelName: string; hotelStars: number;
  hotelRating: string; hotelPic: string; hotelPhotos: string[];
  hotelDesc: string; region: string; subregion: string; country: string;
  price: number; nights: number; flyDate: string; mealName: string;
  roomName: string; operatorName: string; seaDistance: number;
}
interface HotelGroup {
  hotelId: number; hotelName: string; hotelStars: number; hotelRating: string;
  hotelPic: string; hotelPhotos: string[]; hotelDesc: string;
  region: string; subregion: string; country: string; seaDistance: number;
  cheapest: ParsedTour; offers: ParsedTour[];
}

/* ── Helpers ── */
const fmtDDMMYYYY = (s: string) => { const [y,m,d]=s.split('-'); return `${d}.${m}.${y}`; };
const fmtDate = (s: string) => new Date(s).toLocaleDateString('ru-RU',{day:'numeric',month:'short'});
const fmtPrice = (n: number) => Math.round(n).toLocaleString('ru-RU');

/* ── Calendar Component ── */
const CalendarPicker: React.FC<{value:string; onChange:(v:string)=>void; open:boolean; onToggle:()=>void}> = ({value,onChange,open,onToggle}) => {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onToggle(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onToggle]);

  const firstDay = new Date(viewYear, viewMonth, 1);
  let startDow = firstDay.getDay() - 1; if (startDow < 0) startDow = 6;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);

  const prev = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y=>y-1); } else setViewMonth(m=>m-1); };
  const next = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y=>y+1); } else setViewMonth(m=>m+1); };

  return (
    <div className="relative" ref={ref} style={{zIndex: open ? 100 : 'auto'}}>
      <button type="button" onClick={onToggle} className={`input-field text-left flex items-center gap-2 ${value ? 'text-neutral-800' : 'text-neutral-400'}`}>
        <Plane className="w-4 h-4 text-primary-500 flex-shrink-0" />
        <span className="truncate">{value ? fmtDate(value) : 'Выберите дату'}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-neutral-100 p-5 w-[300px] animate-fade-in" style={{zIndex: 100}}>
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prev} className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-semibold text-neutral-800 text-sm">{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button type="button" onClick={next} className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {DAY_NAMES.map(d => <div key={d} className="text-[10px] font-semibold text-neutral-400 text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({length: startDow}).map((_,i) => <div key={`e${i}`} />)}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const day = i + 1;
              const date = new Date(viewYear, viewMonth, day);
              const dateStr = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isPast = date < today;
              const isSelected = dateStr === value;
              return (
                <button key={day} type="button" disabled={isPast}
                  onClick={() => { onChange(dateStr); onToggle(); }}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all
                    ${isPast ? 'text-neutral-200 cursor-not-allowed' : 'hover:bg-primary-50 hover:text-primary-500 cursor-pointer'}
                    ${isSelected ? 'bg-primary-500 text-white hover:bg-primary-600 hover:text-white' : 'text-neutral-700'}
                  `}>{day}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Star Selector ── */
const StarSelector: React.FC<{value:number; onChange:(v:number)=>void}> = ({value, onChange}) => (
  <div className="flex gap-1">
    {[0,3,4,5].map(s => (
      <button key={s} type="button" onClick={() => onChange(s)}
        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
          value === s ? 'bg-primary-500 text-white border-primary-500 shadow-md shadow-primary-500/20' : 'bg-white text-neutral-600 border-neutral-200 hover:border-primary-300'
        }`}>
        {s === 0 ? 'Все' : <span className="flex items-center gap-0.5">{s}<Star className="w-3 h-3 fill-current" /></span>}
      </button>
    ))}
  </div>
);

/* ── Main Component ── */
const SearchTours: React.FC = () => {
  const [departure, setDeparture] = useState(60);
  const [country, setCountry] = useState(4);
  const [departureDate, setDepartureDate] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [nightsFrom, setNightsFrom] = useState(6);
  const [nightsTo, setNightsTo] = useState(14);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [starFilter, setStarFilter] = useState(0);
  const [mealFilter, setMealFilter] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ParsedTour[]>([]);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(9);
  const [selectedTour, setSelectedTour] = useState<ParsedTour | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const parseResults = useCallback((data: any): ParsedTour[] => {
    const tours: ParsedTour[] = [];
    const dec = data.decode || {};
    const hd: Record<string,any> = dec.hotels || {};
    const md: Record<string,any> = dec.meal || {};
    const rd: Record<string,any> = dec.rooms || {};
    const ops = data.operators || [];
    const om: Record<string,string> = {};
    for (const o of ops) om[String(o.id)] = o.name || '';

    for (const bl of (data.block || []))
      for (const h of (bl.hotel || []))
        for (const t of (h.tour || [])) {
          const hi = hd[String(h.id)] || {};
          tours.push({
            id: t.id||'', hotelId: h.id,
            hotelName: hi.name||`Отель #${h.id}`, hotelStars: Number(hi.stars||0),
            hotelRating: hi.rating||'0', hotelPic: hi.pic||'',
            hotelPhotos: hi.photo||[], hotelDesc: hi.desc||'',
            region: hi.region||'', subregion: hi.subregion||'',
            country: hi.country||'', price: Number(t.pr||0),
            nights: Number(t.nt||0), flyDate: t.dt||'',
            mealName: (md[String(t.ml)]||{}).nameshort || (md[String(t.ml)]||{}).name || '',
            roomName: (rd[String(t.rm)]||{}).name || '',
            operatorName: om[String(t.op)]||'',
            seaDistance: Number(hi.seadistance||0),
          });
        }
    tours.sort((a,b) => a.price - b.price);
    return tours;
  }, []);

  // Group hotels: show cheapest per hotel, count offers
  const groupedHotels: HotelGroup[] = React.useMemo(() => {
    let filtered = results;
    if (starFilter > 0) filtered = filtered.filter(t => t.hotelStars === starFilter);
    if (mealFilter > 0) filtered = filtered.filter(t => {
      const ml = t.mealName.toLowerCase();
      if (mealFilter === 7) return ml.includes('включено') || ml.includes('all');
      if (mealFilter === 4) return ml.includes('hb') || ml.includes('ужин') || ml.includes('полупансион');
      if (mealFilter === 3) return ml.includes('bb') || ml.includes('завтрак');
      if (mealFilter === 2) return ml.includes('без питания') || ml.includes('ro');
      return true;
    });

    const map = new Map<number, HotelGroup>();
    for (const t of filtered) {
      const existing = map.get(t.hotelId);
      if (!existing) {
        map.set(t.hotelId, {
          hotelId: t.hotelId, hotelName: t.hotelName, hotelStars: t.hotelStars,
          hotelRating: t.hotelRating, hotelPic: t.hotelPic, hotelPhotos: t.hotelPhotos,
          hotelDesc: t.hotelDesc, region: t.region, subregion: t.subregion,
          country: t.country, seaDistance: t.seaDistance,
          cheapest: t, offers: [t],
        });
      } else {
        existing.offers.push(t);
        if (t.price < existing.cheapest.price) existing.cheapest = t;
      }
    }
    return Array.from(map.values()).sort((a,b) => a.cheapest.price - b.cheapest.price);
  }, [results, starFilter, mealFilter]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!departureDate) { setError('Выберите дату вылета'); return; }
    setLoading(true); setError(''); setResults([]); setVisibleCount(9); setProgress(0);
    if (pollRef.current) clearInterval(pollRef.current);

    try {
      const df = fmtDDMMYYYY(departureDate);
      const dt = new Date(departureDate); dt.setDate(dt.getDate()+3);
      const dtStr = fmtDDMMYYYY(dt.toISOString().split('T')[0]);
      const body: any = { country, departure, datefrom: df, dateto: dtStr, nightsfrom: nightsFrom, nightsto: nightsTo, adults, child: children, currency: 3 };
      if (starFilter > 0) body.stars = starFilter;
      if (mealFilter > 0) body.meal = mealFilter;

      const res = await fetch('/api/search-start', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) });
      const sd = await res.json();
      const rid = sd?.result?.requestid || sd?.requestid;
      if (!rid) throw new Error('fail');

      let cnt = 0;
      pollRef.current = setInterval(async () => {
        cnt++;
        try {
          const r = await fetch(`/api/search-results?requestId=${rid}`);
          const raw = await r.json();
          const d = raw.data || raw;
          setProgress(d.status?.progress || Math.min(cnt*7, 95));
          const tours = parseResults(d);
          if (tours.length > 0) setResults(tours);
          if (d.status?.finished === 1 || (d.status?.progress||0) >= 100 || cnt >= 20) {
            if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null;
            setLoading(false); setProgress(100);
            if (tours.length === 0) setError('Туры не найдены. Попробуйте другие параметры.');
          }
        } catch { if (cnt >= 20) { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; setLoading(false); } }
      }, 3000);
    } catch { setLoading(false); setError('Произошла ошибка. Попробуйте позже.'); }
  };

  const depName = KZ_DEPARTURES.find(d => d.id === departure)?.name || '';
  const countryName = COUNTRIES.find(c => c.code === country)?.name || '';

  /* ── Detail View ── */
  if (selectedTour) {
    const t = selectedTour;
    const group = groupedHotels.find(g => g.hotelId === t.hotelId);
    const offers = group?.offers || [t];
    const photos = t.hotelPhotos.length > 0 ? t.hotelPhotos : (t.hotelPic ? [t.hotelPic] : []);

    return (
      <section id="search" className="py-16 bg-white">
        <div className="container-custom">
          <div className="relative -mt-32 z-10 bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden">
            <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
              <button onClick={() => { setSelectedTour(null); setPhotoIndex(0); }} className="flex items-center gap-1.5 text-primary-500 hover:text-primary-600 font-medium text-sm">
                <ChevronLeft className="w-4 h-4" /> Назад
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5">
              {photos.length > 0 && (
                <div className="lg:col-span-2 relative h-64 lg:h-auto lg:min-h-[480px]">
                  <img src={photos[photoIndex]} alt={t.hotelName} className="w-full h-full object-cover" />
                  {photos.length > 1 && (<>
                    <button onClick={() => setPhotoIndex(i => i>0?i-1:photos.length-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={() => setPhotoIndex(i => i<photos.length-1?i+1:0)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white"><ChevronRight className="w-4 h-4" /></button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">{photos.slice(0,8).map((_,i)=><button key={i} onClick={()=>setPhotoIndex(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i===photoIndex?'bg-white w-4':'bg-white/50'}`}/>)}</div>
                  </>)}
                  {t.hotelStars > 0 && <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-lg px-2 py-1 flex gap-0.5">{[...Array(t.hotelStars)].map((_,i)=><Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400"/>)}</div>}
                </div>
              )}

              <div className="lg:col-span-3 p-6 lg:p-8 overflow-y-auto max-h-[600px]">
                <h2 className="text-xl lg:text-2xl font-bold text-neutral-800 mb-1">{t.hotelName}</h2>
                <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-500 mb-4">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{t.region}{t.subregion?`, ${t.subregion}`:''}, {t.country}</span>
                  {Number(t.hotelRating)>0 && <span className="bg-primary-500 text-white px-2 py-0.5 rounded text-xs font-bold">{t.hotelRating}</span>}
                  {t.seaDistance>0 && <span className="text-xs text-blue-500">~{t.seaDistance}м до моря</span>}
                </div>

                {t.hotelDesc && <p className="text-neutral-500 text-sm leading-relaxed mb-5">{t.hotelDesc}</p>}

                {/* All offers for this hotel */}
                <h3 className="text-sm font-bold text-neutral-700 mb-3">
                  Предложения от {new Set(offers.map(o=>o.operatorName)).size} туроператоров:
                </h3>
                <div className="space-y-2 mb-6">
                  {offers.slice(0,10).map((o,i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                      o.id === t.id ? 'border-primary-500 bg-primary-50' : 'border-neutral-100 hover:border-neutral-200'
                    }`} onClick={() => setSelectedTour(o)}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-neutral-500">
                          <span>{fmtDate(o.flyDate)}</span>
                          <span>{o.nights} ночей</span>
                          <span className="bg-green-50 text-green-700 px-1.5 rounded">{o.mealName}</span>
                        </div>
                        <div className="text-[11px] text-neutral-400 mt-0.5 truncate capitalize">{o.roomName} · {o.operatorName}</div>
                      </div>
                      <div className="text-right ml-3 flex-shrink-0">
                        <div className="text-lg font-bold text-secondary-300">{fmtPrice(o.price)} ₸</div>
                      </div>
                    </div>
                  ))}
                  {offers.length > 10 && <div className="text-xs text-neutral-400 text-center">ещё {offers.length-10} предложений</div>}
                </div>

                <a href={`https://wa.me/77019823126?text=${encodeURIComponent(`Здравствуйте! Интересует тур:\n${t.hotelName} ${t.hotelStars}★\n${t.region}, ${t.country}\nВылет: ${fmtDate(t.flyDate)} из ${depName}\n${t.nights} ночей, ${t.mealName}\nЦена: ${fmtPrice(t.price)} ₸\nОператор: ${t.operatorName}`)}`}
                  target="_blank" rel="noopener noreferrer" className="btn bg-green-500 text-white hover:bg-green-600 w-full justify-center py-3.5 shadow-lg shadow-green-500/20">
                  <MessageCircle className="w-5 h-5 mr-2"/> Забронировать · {fmtPrice(t.price)} ₸
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ── Search View ── */
  return (
    <section id="search" className="py-16 bg-white">
      <div className="container-custom">
        <div className="relative -mt-32 z-10 bg-white rounded-2xl shadow-2xl border border-neutral-100">
          {/* Header gradient */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-700 px-6 md:px-10 py-6 rounded-t-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-white">Поиск туров</h2>
            <p className="text-white/60 text-sm mt-1">Реальные цены от туроператоров Казахстана</p>
          </div>

          <div className="p-6 md:p-10">
            <form onSubmit={handleSearch}>
              {/* Row 1: From → To */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Откуда</label>
                  <div className="relative">
                    <Plane className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-500" />
                    <select className="input-field pl-10 font-medium" value={departure} onChange={e=>setDeparture(Number(e.target.value))}>
                      {KZ_DEPARTURES.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Куда</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-300" />
                    <select className="input-field pl-10 font-medium" value={country} onChange={e=>setCountry(Number(e.target.value))}>
                      {COUNTRIES.map(c=><option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 2: Date + Nights + Tourists */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" style={{overflow:'visible'}}>
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Когда</label>
                  <CalendarPicker value={departureDate} onChange={setDepartureDate} open={calendarOpen} onToggle={()=>setCalendarOpen(!calendarOpen)} />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Ночей</label>
                  <div className="flex gap-1.5 items-center">
                    <select className="input-field text-sm text-center" value={nightsFrom} onChange={e=>setNightsFrom(Number(e.target.value))}>
                      {[3,4,5,6,7,8,9,10].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="text-neutral-300 text-xs">—</span>
                    <select className="input-field text-sm text-center" value={nightsTo} onChange={e=>setNightsTo(Number(e.target.value))}>
                      {[7,8,9,10,12,14,18,21].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Взрослые</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <select className="input-field pl-10 text-sm" value={adults} onChange={e=>setAdults(Number(e.target.value))}>
                      {[1,2,3,4].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5">Дети</label>
                  <select className="input-field text-sm" value={children} onChange={e=>setChildren(Number(e.target.value))}>
                    {[0,1,2,3].map(n=><option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 3: Filters toggle + Stars + Meal */}
              <div className="mb-5">
                <button type="button" onClick={()=>setShowFilters(!showFilters)} className="text-sm text-primary-500 font-medium flex items-center gap-1.5 hover:text-primary-600">
                  <SlidersHorizontal className="w-4 h-4" />
                  {showFilters ? 'Скрыть фильтры' : 'Звёзды, питание и другие фильтры'}
                </button>
                {showFilters && (
                  <div className="mt-3 p-4 bg-neutral-50 rounded-xl space-y-4 animate-fade-in">
                    <div>
                      <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-2">Звёзды отеля</label>
                      <StarSelector value={starFilter} onChange={setStarFilter} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-neutral-400 uppercase tracking-widest mb-2">Питание</label>
                      <div className="flex flex-wrap gap-1.5">
                        {MEAL_OPTIONS.map(m => (
                          <button key={m.value} type="button" onClick={()=>setMealFilter(m.value)}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                              mealFilter===m.value ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-neutral-600 border-neutral-200 hover:border-primary-300'
                            }`}>{m.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Search button */}
              <button type="submit" className="btn btn-secondary flex items-center gap-2 px-10 py-3.5 w-full sm:w-auto text-base" disabled={loading}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                {loading ? `Ищем туры ${progress}%` : 'Найти туры'}
              </button>

              {loading && (
                <div className="mt-4 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary-500 via-secondary-300 to-primary-500 rounded-full transition-all duration-700 animate-pulse" style={{width:`${Math.max(progress,8)}%`}} />
                </div>
              )}
            </form>

            {error && <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 text-center text-sm">{error}</div>}

            {/* Results */}
            {groupedHotels.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-sm text-neutral-500">
                    <strong className="text-neutral-800">{groupedHotels.length}</strong> отелей
                    {results.length !== groupedHotels.length && <span className="text-neutral-400"> · {results.length} предложений</span>}
                  </span>
                  {(starFilter > 0 || mealFilter > 0) && (
                    <button onClick={()=>{setStarFilter(0);setMealFilter(0);}} className="text-xs text-primary-500 hover:text-primary-600">Сбросить фильтры</button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {groupedHotels.slice(0, visibleCount).map((g) => {
                    const t = g.cheapest;
                    const opCount = new Set(g.offers.map(o=>o.operatorName)).size;
                    return (
                      <div key={g.hotelId} className="bg-white rounded-xl overflow-hidden border border-neutral-100 hover:shadow-xl transition-all group cursor-pointer"
                        onClick={()=>{setSelectedTour(t);setPhotoIndex(0);setTimeout(()=>document.getElementById('search')?.scrollIntoView({behavior:'smooth'}),50);}}>
                        {g.hotelPic && (
                          <div className="h-44 overflow-hidden relative">
                            <img src={g.hotelPic} alt={g.hotelName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            {g.hotelStars > 0 && (
                              <div className="absolute top-2.5 right-2.5 bg-white/90 backdrop-blur rounded-md px-1.5 py-0.5 flex gap-0.5">
                                {[...Array(g.hotelStars)].map((_,j)=><Star key={j} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400"/>)}
                              </div>
                            )}
                            {Number(g.hotelRating)>0 && <div className="absolute bottom-2.5 left-2.5 bg-primary-500 text-white px-1.5 py-0.5 rounded text-[11px] font-bold">{g.hotelRating}</div>}
                            <div className="absolute bottom-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white text-primary-500 text-[11px] font-semibold px-2.5 py-1 rounded-lg shadow flex items-center gap-1">
                              Подробнее <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-bold text-neutral-800 text-sm leading-snug mb-0.5 line-clamp-1 group-hover:text-primary-500 transition-colors">{g.hotelName}</h3>
                          <div className="text-[11px] text-neutral-400 mb-3">{g.region}, {g.country || countryName}</div>

                          <div className="flex flex-wrap gap-1.5 text-[11px] mb-3">
                            <span className="bg-neutral-50 text-neutral-500 px-2 py-0.5 rounded">{fmtDate(t.flyDate)}</span>
                            <span className="bg-neutral-50 text-neutral-500 px-2 py-0.5 rounded">{t.nights} ночей</span>
                            {t.mealName && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">{t.mealName}</span>}
                          </div>

                          <div className="flex items-end justify-between">
                            <div>
                              <div className="text-[10px] text-neutral-400">от</div>
                              <div className="text-lg font-bold text-secondary-300">{fmtPrice(t.price)} ₸</div>
                            </div>
                            {g.offers.length > 1 && (
                              <span className="text-[11px] text-primary-500 bg-primary-50 px-2 py-1 rounded-lg font-medium">
                                {g.offers.length} предл. · {opCount} опер.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {visibleCount < groupedHotels.length && (
                  <div className="mt-8 text-center">
                    <button onClick={()=>setVisibleCount(v=>v+9)} className="btn btn-outline px-10">
                      Ещё {Math.min(9,groupedHotels.length-visibleCount)} из {groupedHotels.length}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchTours;
